import React, { useContext, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import {
  Box,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stack,
  TextField
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { toast } from 'react-toastify';
import Toast from '../../../../../../components/Toast';
import { options } from '../../../../../../helpers/toastOptions';
import { useAuth } from '../../../../../../hooks/Auth';
import { useModal } from '../../../../../../hooks/Modal';
import { usePickup } from '../../../../../../hooks/Pickup';
import { SearchMyCollectionsContext } from '../..';
import * as S from './styled';

export const ModalCancelMultiple = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmCancellation, setConfirmCancellation] = useState(false);
  const [completedCancellation, setCompletedCancellation] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [cancelReason, setCancelReason] = useState([]);
  const [invoiceNumbersText, setInvoiceNumbersText] = useState('');

  const { token } = useAuth();
  const { closeModal, Modal } = useModal();
  const { cancelInvoice } = usePickup();

  const {
    cancelReasonList,
    setCancelReasonList,
    invoiceListToModal,
    pickupIdToModal,
    protocolToModal,
    branchParameterizationToModal,
    refreshSearch,
    openOtherCancelToFill
  } = useContext(SearchMyCollectionsContext);

  const maxSM = useMediaQuery('(max-width:768px)');

  const cancelPickupSchema = Yup.object().shape({});

  const {
    handleSubmit,
    control,
    setError,
    getValues,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(cancelPickupSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange'
  });

  const onSubmit = (event) => {
    event.preventDefault();
    let hasError = false;

    cancelReason.forEach((value) => {
      if (
        !value.id ||
        (cancelReasonList?.find((element) => element.cancelId === value.id)
          ?.cancelDescription === 'OUTROS MOTIVOS' &&
          !value?.otherReason)
      ) {
        hasError = true;
      }
    });

    if (hasError) {
      toast(
        <Toast
          title="Atenção!"
          content="Preencha todos os campos."
          type="error"
        />,
        options
      );
    } else {
      setConfirmCancellation(true);
    }
  };

  const handleCancelInvoice = async () => {
    try {
      setIsSubmitting(true);

      const newCancelList = [];

      invoiceListToModal?.forEach((value, index) => {
        const cancel = {
          taxInvoiceId: value.taxInvoiceId,
          cancelReasonId: cancelReason[index].id,
          otherCancelReason: cancelReason[index]?.otherReason || ''
        };
        newCancelList.push(cancel);
      });

      const params = {
        parameterizationId: branchParameterizationToModal,
        pickupId: pickupIdToModal,
        cancelPickupInvoicesRequests: []
      };

      newCancelList.forEach((value) => {
        params.cancelPickupInvoicesRequests.push({
          taxInvoiceId: value.taxInvoiceId,
          cancelReasonId: value.cancelReasonId,
          otherCancelReason: value?.otherCancelReason || ''
        });
      });

      const response = await cancelInvoice({
        data: params,
        token
      });
      if (response.status === 200) {
        setConfirmCancellation(false);
        setCompletedCancellation(true);
        refreshSearch();
      } else {
        throw new Error();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);

      setConfirmCancellation(false);
      setCompletedCancellation(false);

      toast(
        <Toast
          title="Atenção!"
          content="Falha ao cancelar coleta, tente novamente mais tarde."
          type="error"
        />,
        options
      );
    }
  };

  useEffect(() => {
    const newCancelReason = [];

    invoiceListToModal?.forEach(() => {
      newCancelReason.push('');
    });

    setCancelReason(newCancelReason);

    const invoiceNumbers = invoiceListToModal
      ?.map(({ number }) => number)
      .join(', ');

    setInvoiceNumbersText(invoiceNumbers);
  }, []);

  return (
    <Modal preventClose>
      {!confirmCancellation && !completedCancellation && !confirmClose && (
        <form onSubmit={onSubmit}>
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 16,
              maxHeight: '86vh',
              overflowY: 'auto',
              overflowX: 'hidden'
            }}
          >
            <Stack spacing={2} style={{ padding: '8px' }}>
              <S.ModalTitle>Cancelar Coleta</S.ModalTitle>
              <S.SectionTitle>Qual o motivo deste cancelamento?</S.SectionTitle>
              {invoiceListToModal?.map((value, index) => (
                <Stack key={value.taxInvoiceId} spacing={2} direction="column">
                  <S.SectionTitle style={{ textAlign: 'left' }}>
                    NF: {value?.number}
                  </S.SectionTitle>
                  <Controller
                    name="cancelReason"
                    defaultValue=""
                    control={control}
                    render={({ field }) => (
                      <FormControl error={!!errors.cancelReason}>
                        <InputLabel id="cancelReason">
                          Selecione o motivo do cancelamento:
                        </InputLabel>
                        <Select
                          id="cancelReason"
                          label="Selecione o motivo do cancelamento:"
                          {...field}
                          style={{ width: maxSM ? '100%' : 352 }}
                          value={cancelReason[index]?.id}
                          onChange={(event) => {
                            const newCancelReason = [...cancelReason];

                            newCancelReason[index] = {
                              ...newCancelReason[index],
                              id: event.target.value
                            };

                            setCancelReason(newCancelReason);
                          }}
                          error={!!errors.cancelReason}
                        >
                          {cancelReasonList?.map(
                            ({ cancelDescription, cancelId }) => (
                              <MenuItem key={cancelId} value={cancelId}>
                                {cancelDescription}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </FormControl>
                    )}
                  />
                  {cancelReasonList?.find(
                    (element) => element?.cancelId === cancelReason[index]?.id
                  )?.cancelDescription === 'OUTROS MOTIVOS' &&
                    openOtherCancelToFill && (
                      <Controller
                        name="otherCancelReason"
                        defaultValue=""
                        control={control}
                        render={({ field }) => (
                          <TextField
                            name="otherCancelReason"
                            id="otherCancelReason"
                            label="Motivo"
                            {...field}
                            value={cancelReason[index]?.otherReason}
                            onChange={(event) => {
                              const newCancelReason = [...cancelReason];

                              newCancelReason[index] = {
                                ...newCancelReason[index],
                                otherReason: event.target.value
                              };

                              setCancelReason(newCancelReason);
                            }}
                            style={{ width: '100%' }}
                            error={!!errors.otherCancelReason}
                            helperText={errors.otherCancelReason?.message}
                          />
                        )}
                      />
                      // eslint-disable-next-line prettier/prettier
                  )}
                </Stack>
              ))}

              <Box
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 32
                }}
              >
                <Button
                  type="button"
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    setConfirmClose(true);
                  }}
                >
                  Cancelar
                </Button>
                <Button type="submit" variant="contained">
                  Confirmar
                </Button>
              </Box>
            </Stack>
          </Box>
        </form>
      )}

      {confirmCancellation && (
        <Stack spacing={2}>
          <S.ModalTitle>Cancelar Coleta</S.ModalTitle>
          <S.ModalText>
            As Notas Fiscais de número {invoiceNumbersText} do Protocolo{' '}
            {protocolToModal} serão canceladas.
          </S.ModalText>
          <S.ModalText>Deseja Continuar?</S.ModalText>

          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 32
            }}
          >
            <Button
              type="button"
              variant="contained"
              color="attention"
              onClick={() => {
                setCancelReasonList([]);
                closeModal();
              }}
              disabled={isSubmitting}
            >
              Não
            </Button>
            <LoadingButton
              type="button"
              variant="contained"
              onClick={() => {
                handleCancelInvoice();
              }}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Sim
            </LoadingButton>
          </Box>
        </Stack>
      )}

      {completedCancellation && (
        <Stack spacing={2}>
          <S.ModalTitle>Cancelamento Concluído</S.ModalTitle>
          <S.ModalText>
            Sua solicitação para cancelamento das Notas Fiscais de número{' '}
            {invoiceNumbersText} da coleta de Protocolo {protocolToModal} foi
            realizada com sucesso.
            <br />
            <br />
            Em breve receberá um e-mail!
          </S.ModalText>

          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 32
            }}
          >
            <Button
              type="button"
              variant="contained"
              onClick={() => {
                setCancelReasonList([]);
                closeModal();
              }}
            >
              Ok
            </Button>
          </Box>
        </Stack>
      )}

      {confirmClose && (
        <Stack spacing={2}>
          <S.ModalTitle>Atenção</S.ModalTitle>

          <S.ModalText>
            Deseja cancelar a solicitação de cancelamento?
          </S.ModalText>

          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 32
            }}
          >
            <Button
              type="button"
              variant="contained"
              color="attention"
              onClick={() => setConfirmClose(false)}
            >
              Não
            </Button>
            <Button
              type="button"
              variant="contained"
              color="primary"
              onClick={() => {
                setCancelReasonList([]);
                closeModal();
              }}
            >
              Sim
            </Button>
          </Box>
        </Stack>
      )}
    </Modal>
  );
};
